import React, { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Shows an email the way an inbox would: the mail's own 600px card at true size, shrunk
 * only when the pane is narrower than it, and the frame grows to the mail's full height.
 * There is never a scrollbar inside the preview — the pane around it scrolls as one
 * surface, and only if the mail is taller than the pane.
 *
 * `sandbox="allow-same-origin"` (no scripts) is what lets us read the document's height.
 * The campaign templates are static HTML with no scripts, so nothing can run in there.
 */
export const EMAIL_WIDTH = 640; // the 600px card plus the template's own side padding
/** Same page colour the email templates paint behind the card (campaignTemplates PAGE_BG). */
export const EMAIL_CANVAS = '#F4F6F9';

const BLANK = '<!doctype html><html><body style="margin:0;background:#F4F6F9"></body></html>';

/**
 * `zoom`: null fits the mail to the pane (never larger than life); a number is a fixed scale —
 * 1.25 shows the fine print, 0.75 the whole letter — and the pane around it scrolls sideways.
 */
/** A phone-width frame: the mail's own media query takes over and the card fills the screen. */
export const MOBILE_WIDTH = 390;

export function EmailPreview({ html, title = 'Email preview', minHeight = 320, className = '', zoom = null, device = 'desktop' }) {
  const wrapRef = useRef(null);
  const frameRef = useRef(null);
  const frameWidth = device === 'mobile' ? MOBILE_WIDTH : EMAIL_WIDTH;
  const [scale, setScale] = useState(zoom ?? 1);
  const [height, setHeight] = useState(minHeight);

  const measure = useCallback(() => {
    const wrap = wrapRef.current;
    const frame = frameRef.current;
    if (!wrap || !frame) return;
    const width = wrap.parentElement?.clientWidth || wrap.clientWidth || frameWidth;
    setScale(zoom ?? Math.min(1, width / frameWidth));
    try {
      const doc = frame.contentDocument;
      const h = doc ? Math.max(doc.documentElement?.scrollHeight || 0, doc.body?.scrollHeight || 0) : 0;
      if (h > 0) setHeight(Math.max(minHeight, h));
    } catch {
      /* cross-origin document — keep the last measured height */
    }
  }, [minHeight, zoom, frameWidth]);

  useEffect(() => {
    measure();
  }, [measure]);

  // Paint the mail. Setting srcdoc reloads the frame, and onLoad re-measures.
  useEffect(() => {
    if (frameRef.current) frameRef.current.srcdoc = html || BLANK;
  }, [html]);

  // Re-fit whenever the pane changes width (window resize, column resize, modal size).
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => measure());
    ro.observe(wrap);
    return () => ro.disconnect();
  }, [measure]);

  return (
    <div
      ref={wrapRef}
      className={`mx-auto overflow-hidden ${zoom ? '' : 'w-full'} ${className}`}
      style={{
        width: zoom ? Math.ceil(frameWidth * scale) : undefined,
        maxWidth: zoom ? undefined : frameWidth,
        height: Math.ceil(height * scale),
        background: EMAIL_CANVAS,
      }}
    >
      <iframe
        ref={frameRef}
        title={title}
        sandbox="allow-same-origin"
        scrolling="no"
        onLoad={measure}
        style={{
          display: 'block',
          width: frameWidth,
          height,
          border: 0,
          background: EMAIL_CANVAS,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          pointerEvents: 'none',
        }}
      />
    </div>
  );
}

export default EmailPreview;
